import { useMemo } from 'react';
import { BiSolidDownArrow, BiSolidUpArrow } from 'react-icons/bi';
import { IoArrowForwardOutline } from 'react-icons/io5';

import { TACTICS_ARR } from '../../utils/constants';
// eslint-disable-next-line import/order
import { IconSelect, TacticsIds } from '../TacticPicker/icons';
import { Modal } from '@/components';
import { TacticAssistant } from '@/types/team';
import {
  PLAYER_NAME_SINGULAR_POSITION_BY_ABBREVIATION,
  PLAYER_POSITION_LABEL_BY_ABBREVIATION,
} from '@/utils';

type SwapPosition = 'DEL' | 'VOL' | 'DEF';

interface SwapGroup {
  sale?: SwapPosition;
  entra?: SwapPosition;
  count: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentTacticId: TacticsIds;
  newTacticId: TacticsIds;
  tacticAssistantResponse?: TacticAssistant;
}

const positionLabel = (position: SwapPosition, count: number) =>
  count > 1
    ? PLAYER_POSITION_LABEL_BY_ABBREVIATION[position]
    : PLAYER_NAME_SINGULAR_POSITION_BY_ABBREVIATION[position];

const TRANSFER_ORDINAL_WORDS: Record<number, string> = {
  2: 'segunda',
  3: 'tercer',
};

export const ModalTacticChange = ({
  isOpen,
  onClose,
  currentTacticId,
  newTacticId,
  tacticAssistantResponse,
}: Props) => {
  const swapSteps = useMemo(() => {
    if (!newTacticId) return [];
    return (
      tacticAssistantResponse?.respuestas?.find(
        (respuesta) => respuesta?.destino === Number(newTacticId)
      )?.paths?.[0] ?? []
    );
  }, [tacticAssistantResponse, newTacticId]);

  const swapGroups = useMemo(() => {
    const groups = new Map<string, SwapGroup>();
    swapSteps.forEach((step) => {
      const key = `${step.sale}-${step.entra}`;
      const existing = groups.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        groups.set(key, { sale: step.sale, entra: step.entra, count: 1 });
      }
    });
    return Array.from(groups.values());
  }, [swapSteps]);

  return (
    <Modal
      bottomSheetClassName="px-[1rem] pb-12 pt-0"
      className="py-[1.75rem]"
      controlledOnClose={onClose}
      isControlledOpen={isOpen}
      render={() => (
        <div className="text-center">
          <p className="text-sm font-medium mt-4">
            Para jugar con la táctica{' '}
            <span className="font-bold text-gdt-blue">
              {TACTICS_ARR[newTacticId]?.join('-')}
            </span>{' '}
            tenés que reemplazar
            <br />
            {swapGroups.map((group, index) => (
              <span key={index}>
                {index > 0 && ' y '}
                <span className="font-bold text-gdt-blue">
                  {group.count}{' '}
                  {group.sale && positionLabel(group.sale, group.count)}
                </span>{' '}
                por{' '}
                <span className="font-bold text-gdt-blue">
                  {group.count}{' '}
                  {group.entra && positionLabel(group.entra, group.count)}
                </span>
              </span>
            ))}
          </p>
          <div className="flex justify-center items-center mt-5 mb-2 mx-auto">
            <div className="border py-4 px-6 rounded-lg">
              <p className="font-bold mb-3 leading-none">
                {TACTICS_ARR[currentTacticId]?.join('-')}
              </p>
              <IconSelect id={currentTacticId} />
            </div>
            <IoArrowForwardOutline className="mx-1" size={20} />
            <div className="border py-4 px-6 rounded-lg">
              <p className="font-bold mb-3 leading-none">
                {TACTICS_ARR[newTacticId || 0]?.join('-')}
              </p>
              <IconSelect id={newTacticId} />
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-2 w-[255px] mx-auto">
            {swapSteps.map((step, index) => (
              <div key={index} className="flex w-full justify-center">
                <div className="flex flex-1 justify-center items-center">
                  <div className="flex w-[70px] items-center justify-center gap-1 rounded bg-gdt-status-red-light py-1 text-xs font-bold text-gdt-status-red">
                    <BiSolidDownArrow />
                    {step.sale} -1
                  </div>
                </div>
                <div className="flex flex-1 justify-center items-center">
                  <div className="flex w-[70px] items-center justify-center gap-1 rounded bg-gdt-status-green-light py-1 text-xs font-bold text-gdt-status-green">
                    <BiSolidUpArrow />
                    {step.entra} +1
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs font-semibold mt-5">
            {swapSteps.length > 1 ? (
              <>
                Al confirmar la{' '}
                {TRANSFER_ORDINAL_WORDS[swapSteps.length] ??
                  `${swapSteps.length}°`}{' '}
                transferencia tendrás la táctica seleccionada.
              </>
            ) : (
              <>
                Tu táctica se cambiará automáticamente
                <br />
                al confirmar la transferencia.
              </>
            )}
          </p>
        </div>
      )}
      size="xs"
      title="Cambio de táctica"
      variant="controlled"
    />
  );
};
